import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from '../../../services/dashboard.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { InventoryService } from '../../../services/inventory.service';
import { PrintService } from '../../../services/print.service';
import { ReportsService } from '../../../services/reports.service';

interface RepRow {
	salesmancode: string;
	salesmanname: string;
	sales: number;
	units: number;
	invoices: number;
	prior_sales: number;
	active_customers: number;
	yoy: number | null;
}

@Component({
	selector: 'app-team-leaderboard',
	templateUrl: './team-leaderboard.component.html',
	styleUrls: ['./team-leaderboard.component.scss'],
})
export class TeamLeaderboardComponent implements OnInit, OnDestroy {

	reps: RepRow[] = [];
	loading = true;
	exporting = false;
	datefrom = '';
	dateto = '';
	total = 0;
	/** Server capped the response to the Top 3 (regular-rep view). */
	limited = false;
	view: 'bars' | 'table' = 'bars';
	private lastFrom: string | undefined;
	private lastTo: string | undefined;

	color = 'blue';
	private destroy$ = new Subject<void>();

	readonly presets = [
		{ label: 'MTD',   from: null, to: null },          // null = server default (current month)
		{ label: '30d',   days: 30 },
		{ label: '60d',   days: 60 },
		{ label: '90d',   days: 90 },
		{ label: 'YTD',   ytd: true },
		{ label: 'Custom', custom: true },
	];
	activePreset = 'MTD';
	showPresets = false;
	customFrom = '';
	customTo = '';

	// ── Brand / category narrowing ────────────────────────────────────────────
	showFilters = false;
	brands = new UntypedFormControl([]);
	categories = new UntypedFormControl([]);
	salespeople = new UntypedFormControl([]);
	brandList: any[] = [];
	categoryList: any[] = [];
	repList: any[] = [];
	/** Unnarrowed category list, restored when the brand filter is cleared. */
	private allCategories: any[] = [];
	filtered = false;

	private static readonly THEME_PALETTE: Record<string, { light: [number, number, number]; base: [number, number, number]; dark: [number, number, number] }> = {
		purple:  { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		primary: { light: [206, 147, 216], base: [156,  39, 176], dark: [ 74,  20, 140] },
		blue:    { light: [144, 202, 249], base: [ 33, 150, 243], dark: [ 25, 118, 210] },
		info:    { light: [128, 222, 234], base: [  0, 188, 212], dark: [  0,  96, 100] },
		green:   { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		success: { light: [165, 214, 167], base: [ 76, 175,  80], dark: [ 27,  94,  32] },
		orange:  { light: [255, 204, 128], base: [255, 152,   0], dark: [230,  81,   0] },
		warning: { light: [255, 224, 130], base: [255, 193,   7], dark: [255, 143,   0] },
		red:     { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		danger:  { light: [239, 154, 154], base: [244,  67,  54], dark: [183,  28,  28] },
		rose:    { light: [244, 143, 177], base: [233,  30,  99], dark: [136,  14,  79] },
	};

	get palette() {
		return TeamLeaderboardComponent.THEME_PALETTE[this.color] || TeamLeaderboardComponent.THEME_PALETTE['blue'];
	}

	get themeVars(): { [key: string]: string } {
		const p = this.palette;
		const rgb  = (c: number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
		const rgba = (c: number[], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
		return {
			'--tl-accent':        rgb(p.base),
			'--tl-accent-dark':   rgb(p.dark),
			'--tl-accent-light':  rgb(p.light),
			'--tl-accent-tint':   rgba(p.base, 0.07),
			'--tl-accent-tint-2': rgba(p.base, 0.14),
			'--tl-accent-border': rgba(p.base, 0.20),
			'--tl-accent-bg':     rgba(p.light, 0.16),
		};
	}

	constructor(
		private dashboardService: DashboardService,
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private printService: PrintService,
		private reportsService: ReportsService,
	) {
		this.color = 'rose';
	}

	ngOnInit(): void {
		this.inventoryService.getBrands()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => { this.brandList = Array.isArray(r) ? r : []; });

		this.inventoryService.getItemCategories()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.allCategories = Array.isArray(r) ? r : [];
				this.categoryList = this.allCategories;
			});

		// reports/salespeople self-limits to the caller's own code for sales-channel
		// users, so a rep can never populate this with the rest of the team.
		this.reportsService.getSalesPeople()
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => { this.repList = Array.isArray(r) ? r : []; });

		// Picking brands narrows the category list to what those brands carry.
		this.brands.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.narrowCategories());

		this.load();
	}
	ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

	load(from?: string, to?: string): void {
		this.loading = true;
		// Remember the window so an Apply from the filter drawer keeps the preset.
		// Undefined is meaningful here — it means "let the server default to MTD".
		this.lastFrom = from;
		this.lastTo = to;
		this.dashboardService.getTeamLeaderboard(from, to, this.brands.value || [], this.categories.value || [], this.salespeople.value || [])
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (res: any) => {
					this.reps      = (res.data || []).filter((r: RepRow) => r.sales > 0);
					this.datefrom  = res.datefrom || '';
					this.dateto    = res.dateto || '';
					this.total     = res.total || 0;
					this.limited   = res.limited === true;
					this.filtered  = res.filtered === true;
					this.loading   = false;
				},
				error: () => { this.loading = false; }
			});
	}

	selectPreset(p: any): void {
		// The refresh button passes only a label; resolve it back to the real
		// preset so refreshing on "90d" doesn't silently snap back to MTD.
		const preset = this.presets.find(x => x.label === p?.label) || p;
		this.activePreset = preset.label;
		p = preset;
		if (p.custom) {
			// Seed the pickers from the window on screen and leave the row
			// open — applyCustom() runs the query once both dates are set.
			if (!this.customFrom) { this.customFrom = this.datefrom; }
			if (!this.customTo)   { this.customTo   = this.dateto; }
			return;
		}
		this.showPresets = false;
		const today = new Date();
		if (p.ytd) {
			const from = today.getFullYear() + '-01-01';
			const to   = today.toISOString().slice(0, 10);
			this.load(from, to);
		} else if (p.days) {
			const from = new Date(today.getTime() - p.days * 864e5).toISOString().slice(0, 10);
			const to   = today.toISOString().slice(0, 10);
			this.load(from, to);
		} else {
			this.load(); // MTD default
		}
	}

	applyCustom(): void {
		if (!this.customFrom || !this.customTo) { return; }
		// ISO strings compare correctly; silently un-reverse a backwards range.
		if (this.customFrom > this.customTo) {
			const t = this.customFrom;
			this.customFrom = this.customTo;
			this.customTo = t;
		}
		this.activePreset = 'Custom';
		this.showPresets = false;
		this.load(this.customFrom, this.customTo);
	}

	refresh(): void {
		if (this.activePreset === 'Custom' && this.customFrom && this.customTo) {
			this.load(this.customFrom, this.customTo);
			return;
		}
		this.selectPreset({ label: this.activePreset });
	}

	exportXls(): void {
		if (!this.reps.length || this.exporting) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportTable('xls')),
			filename: this.exportFilename(),
			title: 'Sales Team',
			subtitle: this.exportSubtitle(),
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	exportPdf(): void {
		if (!this.reps.length || this.exporting) { return; }
		this.exporting = true;
		const data = {
			content: this.globalSearchService.base64encode(this.buildExportTable('pdf')),
			filename: this.exportFilename(),
			title: 'Sales Team',
			subtitle: this.exportSubtitle(),
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		}, () => { this.exporting = false; });
	}

	/**
	 * One table for both paths. Money keeps its $ and commas — apir/print's xls
	 * path strips them before PHPExcel, so Excel still gets real numbers.
	 */
	private buildExportTable(kind: 'xls' | 'pdf'): string {
		const esc = (s: any) => ('' + (s ?? '')).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		const money = (v: any) => '$' + (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		const whole = (v: any) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

		const cols: Array<{ label: string; num?: boolean; cell: (r: RepRow, i: number) => string }> = [
			{ label: '#',         cell: (_r, i) => '' + (i + 1) },
			{ label: 'Rep',       cell: (r) => esc(r.salesmanname) },
			{ label: 'Sales',     num: true, cell: (r) => money(r.sales) },
			{ label: 'Units',     num: true, cell: (r) => whole(r.units) },
			{ label: 'Invoices',  num: true, cell: (r) => '' + r.invoices },
			{ label: 'Customers', num: true, cell: (r) => '' + r.active_customers },
			{ label: 'Prior Yr',  num: true, cell: (r) => money(r.prior_sales) },
			{ label: 'YoY',       num: true, cell: (r) => r.yoy === null ? '' : this.formatPct(r.yoy) },
		];
		if (!this.limited) {
			cols.push({ label: 'Share', num: true, cell: (r) => this.barPct(r.sales).toFixed(1) + '%' });
		}

		const num = ' style="text-align:right"';
		let html = '';
		if (kind === 'pdf') {
			html += '<style>'
				+ 'table{width:100%;border-collapse:collapse;font-size:11px;font-family:Helvetica,Arial,sans-serif}'
				+ 'th{background-color:#3B4A5C;color:#FFFFFF;padding:5px 8px;border:1px solid #3B4A5C}'
				+ 'td{padding:4px 8px;border:1px solid #D8DDE3}'
				+ 'tr.z td{background-color:#F3F5F7}'
				+ 'tr.tot td{background-color:#E2E7EC;font-weight:bold;border-top:2px solid #3B4A5C}'
				+ '</style><table>';
		} else {
			html += '<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;font-size:11px">';
		}

		html += '<thead><tr>';
		cols.forEach((c) => {
			html += '<th style="background-color:#3B4A5C;color:#FFFFFF;text-align:' + (c.num ? 'right' : 'left') + '">' + esc(c.label) + '</th>';
		});
		html += '</tr></thead><tbody>';

		this.reps.forEach((r, i) => {
			html += '<tr' + (kind === 'pdf' && i % 2 ? ' class="z"' : '') + '>'
				+ cols.map((c) => '<td' + (c.num ? num : '') + '>' + c.cell(r, i) + '</td>').join('')
				+ '</tr>';
		});

		// Top-3-restricted reps never see team totals on screen; the file
		// matches that.
		if (!this.limited) {
			const units = this.reps.reduce((s, r) => s + (Number(r.units) || 0), 0);
			const inv   = this.reps.reduce((s, r) => s + r.invoices, 0);
			html += '<tr class="tot" style="font-weight:bold;background-color:#E2E7EC">'
				+ '<td></td><td>TOTAL</td>'
				+ '<td' + num + '>' + money(this.total) + '</td>'
				+ '<td' + num + '>' + whole(units) + '</td>'
				+ '<td' + num + '>' + inv + '</td>'
				+ '<td></td><td></td><td></td><td></td>'
				+ '</tr>';
		}

		return html + '</tbody></table>';
	}

	private exportFilename(): string {
		return 'sales-team-' + (this.datefrom || '') + '-to-' + (this.dateto || '');
	}

	/** Date window plus active filters, so the sheet says what slice it covers. */
	private exportSubtitle(): string {
		const parts = [this.datefrom + ' to ' + this.dateto];
		const names = (list: any[], sel: any[], id: string, label: string) =>
			(sel || []).map((v: any) => {
				const m = (list || []).find((x: any) => x[id] === v);
				return m ? m[label] : v;
			});
		const b = names(this.brandList, this.brands.value, 'manufacturer_id', 'name');
		if (b.length) { parts.push('Brands: ' + b.join(', ')); }
		const c = names(this.allCategories, this.categories.value, 'categoryid', 'categorydescription');
		if (c.length) { parts.push('Categories: ' + c.join(', ')); }
		const r = names(this.repList, this.salespeople.value, 'salesmancode', 'salesmanname');
		if (r.length) { parts.push('Reps: ' + r.join(', ')); }
		return parts.join('  |  ');
	}

	/** Re-run the current period with the current brand/category filters. */
	applyFilters(): void {
		this.load(this.lastFrom, this.lastTo);
	}

	clearFilters(): void {
		this.brands.setValue([], { emitEvent: false });
		this.categories.setValue([], { emitEvent: false });
		this.salespeople.setValue([], { emitEvent: false });
		this.categoryList = this.allCategories;
		this.load(this.lastFrom, this.lastTo);
	}

	get filterCount(): number {
		return (this.brands.value?.length || 0)
			+ (this.categories.value?.length || 0)
			+ (this.salespeople.value?.length || 0);
	}

	get brandsNarrowed(): boolean {
		return !!(this.brands.value && this.brands.value.length);
	}

	private narrowCategories(): void {
		const brands = this.brands.value || [];

		if (!brands.length) {
			this.categoryList = this.allCategories;
			this.pruneCategories();
			return;
		}

		this.inventoryService.getBrandCategories({ brands })
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.categoryList = Array.isArray(r) ? r : [];
				this.pruneCategories();
			});
	}

	/** Drop selected categories the narrowed brand set no longer carries. */
	private pruneCategories(): void {
		const available = this.categoryList.map((c: any) => c.categoryid ?? c.value);
		const keep = (this.categories.value || []).filter((v: any) => available.indexOf(v) > -1);
		if (keep.length !== (this.categories.value || []).length) {
			this.categories.setValue(keep, { emitEvent: false });
		}
	}

	barPct(sales: number): number {
		// Top-3 view: total is only the sum of 3 rows, so scale bars to the
		// leader instead of share-of-total.
		const base = this.limited ? (this.reps[0]?.sales || 0) : this.total;
		return base > 0 ? Math.min((sales / base) * 100, 100) : 0;
	}

	barColor(i: number): string {
		const p = this.palette;
		const n = Math.max(this.reps.length - 1, 1);
		const t = i / n;
		const lerp = (a: number, b: number) => Math.round(a + (b - a) * t);
		return `rgb(${lerp(p.light[0], p.dark[0])},${lerp(p.light[1], p.dark[1])},${lerp(p.light[2], p.dark[2])})`;
	}

	formatPct(v: number | null): string {
		if (v === null) { return '—'; }
		const sign = v >= 0 ? '+' : '';
		return sign + (v * 100).toFixed(0) + '%';
	}

	fmt(v: number): string {
		if (v >= 1_000_000) { return '$' + (v / 1_000_000).toFixed(1) + 'M'; }
		if (v >= 1000)      { return '$' + (v / 1000).toFixed(1) + 'k'; }
		return '$' + v.toFixed(0);
	}
}
